import { apiUpdateSensor } from 'apis/SensorApi';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { SENSOR_STATUS_ALERT } from 'constants/config';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE, TEXT_SENSOR_GROUP_CLICKED } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useSensors } from 'context/SensorsContext';
import { useAuth } from 'context/AuthContext';
import { LockState } from 'models';
import PropTypes from 'prop-types';
import React from 'react';
import Popup from '../../components/Popup';
import "./MapPage.css";
import MapView from './components/MapView';
import { PopupContent } from './components/SensorInfoCard';
import SensorSearchbar from './components/SensorSearchbar';
import { SensorType } from 'models';
import { PAGE_LINK, PAGE_BASE } from 'utils/constanst';
import { useNavigate } from 'react-router-dom';

const propTypes = {
  isTop: PropTypes.bool,
  onMapLoad: PropTypes.func,
  onViewSensorDetail: PropTypes.func,
  onSendMail: PropTypes.func,
  onZoomChange: PropTypes.func,
};
const defaultProps = {
  isTop: true,
  onMapLoad: (map) => { },
  onViewSensorDetail: (sensor) => { },
  onSendMail: (sensor) => { },
  onZoomChange: () => { },
};
const MapPage = (props) => {
  /*** States and Variables ***/
  const mapRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const geoJsonRef = React.useRef(null);
  const mouseEventRef = React.useRef(null);
  const [selectedSensorId, setSelectedSensorId] = React.useState('');
  const { generalSetting, defaultBounding } = useGeneralSetting();
  const [loading, setLoading] = React.useState(false);
  const { error } = useDialog();
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  const [sensorInfo, setSensorInfo] = React.useState(null);
  const [hasWarningSensor, setHasWarningSensor] = React.useState(false);
  const { sensors, layers, analyze } = useSensors();
  const { sensorGroups } = useSensorGroups();
  const [zoom, setZoom] = React.useState(5); // list of current sensors shown on map
  const [isMouseOutsidePopup, setIsMouseOutsidePopup] = React.useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = React.useState(false);
  const navigate = useNavigate();

  /*** Processing ***/
  React.useEffect(() => {
    if (mapRef.current && defaultBounding) {
      // Use user's coordinates if available, otherwise use default
      const userLat = currentUser?.data?.latitude;
      const userLng = currentUser?.data?.longitude;
      const userZoom = currentUser?.data?.mapZoomRatio;

      const lat = (userLat && !isNaN(Number(userLat))) ? Number(userLat) : defaultBounding.lat;
      const lng = (userLng && !isNaN(Number(userLng))) ? Number(userLng) : defaultBounding.lng;
      const zoom = (userZoom && !isNaN(Number(userZoom))) ? Number(userZoom) : defaultBounding.zoom;

      mapRef.current.setView(
        {
          lat: lat,
          lon: lng
        },
        zoom);
    }
  }, [defaultBounding, currentUser?.data?.latitude, currentUser?.data?.longitude, currentUser?.data?.mapZoomRatio]);

  React.useEffect(() => {
    if (sensorInfo?.length) {
      const ids = sensorInfo.map((item) => item.id);
      const newSensor = sensors.filter((item) => ids.includes(item.id));
      if (newSensor) setSensorInfo(newSensor);
    }
    const warningStatus = sensors.filter((sensor) => {
      const targetGroup = sensorGroups.find(g => g.id === sensor.groupId);
      if (targetGroup?.messages[sensor.status] && targetGroup.messages[sensor.status].value === SENSOR_STATUS_ALERT) return true;
      if (generalSetting?.alertOn && sensor.isLocked) return true;
      return false;
    })
    setHasWarningSensor(!!warningStatus.length);
  }, [sensors, sensorGroups, generalSetting]);

  React.useEffect(() => {
    const handleMouseMove = (event) => {
      if (popupRef.current) {
        const rect = popupRef.current.getBoundingClientRect();
        const { clientX, clientY } = event;

        // Kiểm tra nếu con trỏ chuột nằm ngoài popup
        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          setIsMouseOutsidePopup(true);
        } else {
          setIsMouseOutsidePopup(false);
        }
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      setSelectedSensorId('');
      setSensorInfo(null);
    };
  }, []);

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      setIsVideoFullscreen(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);


  React.useEffect(() => {
    if (!props.isTop) {
      setIsOpen(false);
      setSensorInfo(null);
      setSelectedSensorId('');
      setIsMouseOutsidePopup(false);
      setIsVideoFullscreen(false);
    }
  }, [props.isTop])

  /*** Sub Components ***/
  /*** Event Handlers ***/

  const calculateOffsetLng = (lng, lat, newZoom) => {
    const centerPoint = mapRef.current.latLngToContainerPoint([lat, lng]);
    const scaleFactor = Math.pow(2, newZoom - mapRef.current.getZoom());
    const offsetX = (window.innerWidth / 4) / scaleFactor;

    const targetPoint = { x: centerPoint.x + offsetX, y: centerPoint.y };
    const newLatLng = mapRef.current.containerPointToLatLng(targetPoint);
    return newLatLng.lng - lng;
  };

  const onViewportChange = React.useCallback(
    (e) => {
      const map = e.target;
      const newZoom = map.getZoom();
      setZoom(newZoom);
      if (props.onZoomChange) {
        props.onZoomChange(newZoom);
      }
    },
    [layers, props]
  );

  const onMarkerClick = React.useCallback(async (sensor) => {
    if (sensor.id.includes('#')) {
      if (mapRef.current) {
        const currentZoom = mapRef.current.getZoom();
        const newZoom = Math.min(currentZoom + 2, 18);
        mapRef.current.flyTo({ lat: sensor.lat, lon: sensor.lng }, newZoom);
      }
      return;
    }
    const targetGroup = sensorGroups.find(g => g.id === sensor.groupId || sensor.groupId.includes(g.id));
    setIsOpen(true);
    setSelectedSensorId(sensor.id);
    mouseEventRef.current = 'click';
    setSensorInfo([sensor]);
    if (mapRef.current) {
      if (targetGroup?.type === SensorType.CAMERA) {
        navigate(`${PAGE_LINK[PAGE_BASE]}/${sensor.id}`);
      }
    }
  }, [sensors, generalSetting, navigate, sensorGroups])

  const onMarkerMouseOver = React.useCallback((sensor) => {
    setIsOpen(true);
    setSelectedSensorId(prevSelectedSensorId => {
      if ((!mouseEventRef.current || mouseEventRef.current === 'click') && sensor.id !== prevSelectedSensorId) {
        mouseEventRef.current = 'mouseover';
      }
      return sensor.id; // Cập nhật state một cách an toàn
    });
    if (sensor.id.includes('#')) {
      const ids = analyze(sensor.id);
      const group = sensors.filter(s => ids.includes(s.id));
      setSensorInfo(group);
    } else {
      setSensorInfo([sensor]);
    }
  }, [sensors, sensorGroups, analyze])

  const onMarkerMouseOut = React.useCallback((sensor) => {
    if (mouseEventRef.current === "mouseover" && isMouseOutsidePopup) {
      setIsOpen(false);
      setSensorInfo(null);
    }
  }, [isMouseOutsidePopup])

  const onPopupMouseLeave = React.useCallback(() => {
    if (mouseEventRef.current === 'mouseover' && !isVideoFullscreen) {
      setIsOpen(false);
      setSensorInfo(null);
    }
  }, [isVideoFullscreen])

  const onPopupClose = () => {
    setIsOpen(false);
    setSensorInfo(null);
    setSelectedSensorId('');
    setIsMouseOutsidePopup(false);
    setIsVideoFullscreen(false);
    mouseEventRef.current = null;
  }

  const onMapClick = React.useCallback(() => {
    setSelectedSensorId('');
  }, [])

  const onMapLoad = React.useCallback(async (e) => {
    const map = e.target;
    mapRef.current = map;
    props.onMapLoad(map);
    setZoom(map.getZoom());
  }, [props.onMapLoad])

  const onSensorClick = async (sensor) => {
    mapRef.current.flyTo({ lat: sensor.lat, lon: sensor.lng }, mapRef.current.getZoom());
  }

  const onUnlockSensor = async (sensor) => {
    if (loading) return;
    setLoading(true);
    const result = await apiUpdateSensor({
      id: sensor.id,
      isLocked: LockState.UNLOCKED
    });
    setLoading(false);
    if (!result.success) {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
    }
  }

  // cache sensors by id for quick access
  const sensorsById = React.useMemo(() => {
    const map = new Map();
    sensors.forEach((sensor) => {
      map.set(sensor.id, sensor);
    });
    return map;
  }, [sensors]);

  /*** Main Render ***/
  return <div className='map_page_container'>
    <MapView
      markers={layers[Math.floor(zoom + 3)]}
      geoData={defaultBounding?.boundingBox}
      geoJsonRef={geoJsonRef}
      selectedId={selectedSensorId}
      zoom={zoom}
      sensorsById={sensorsById}
      onMarkerClick={onMarkerClick}
      onMarkerMouseOver={onMarkerMouseOver}
      onMarkerMouseOut={onMarkerMouseOut}
      onViewportChange={onViewportChange}
      onMapClick={onMapClick}
      onMapLoad={onMapLoad}/>
    {
      <>
        {generalSetting?.menuType === 'drawer' && (
          <SensorSearchbar
            onClick={onSensorClick}
            onUnlock={onUnlockSensor}
            setSensorInfo={setSensorInfo}
            sensorInfo={sensorInfo}
            onViewSensorDetail={props.onViewSensorDetail}
            onSendMail={props.onSendMail}
            hasWarningSensor={hasWarningSensor}
          />
        )}
        <Popup
          ref={popupRef}
          isOpen={isOpen}
          isDrag={false}
        >
          <PopupContent
            setIsOpen={setIsOpen}
            sensors={sensorInfo}
            onUnlockSensor={onUnlockSensor}
            onCLose={onPopupClose}
            onPopupMouseLeave={onPopupMouseLeave}
            onViewSensorDetail={(sensor, state) => { props.onViewSensorDetail(sensors.find(s => s.id === sensor.id), state) }}
            onSendMail={props.onSendMail}
          />
        </Popup>
      </>
    }

    {loading && <FanLoadingIcon size={80} />}
  </div>;
}

MapPage.propTypes = propTypes;
MapPage.defaultProps = defaultProps;
export default MapPage;